import { useEffect, useRef } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  keywords?: string;
  jsonLd?: object | object[];
  ogImage?: string;
}

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

export default function SEOHead({
  title,
  description,
  canonical,
  keywords,
  jsonLd,
  ogImage,
}: SEOHeadProps) {
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    if (!jsonLd) return;

    if (scriptRef.current) {
      scriptRef.current.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', canonical || title || 'page');
    const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    script.textContent = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
    document.head.appendChild(script);
    scriptRef.current = script;

    return () => {
      if (scriptRef.current) {
        scriptRef.current.remove();
        scriptRef.current = null;
      }
    };
  }, [jsonLd, canonical, title]);

  const fullCanonical = canonical ? `${SITE_URL}${canonical}` : undefined;
  const image = ogImage || 'https://public.readdy.ai/ai/img_res/7ed708d0-0e46-4404-b490-9c89f3f516ab.png';

  return (
    <>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {fullCanonical && <link rel="canonical" href={fullCanonical} />}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {fullCanonical && <meta property="og:url" content={fullCanonical} />}
      <meta property="og:image" content={image} />
      <meta property="og:type" content="website" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={image} />
      <meta name="last-modified" content={new Date().toISOString().split('T')[0]} />
    </>
  );
}

export { SITE_URL };
